/**
 * role-routes.ts
 *
 * Single source of truth for role-based routing after login.
 *
 * - `getPostLoginRedirect` drives the redirect in auth-page.tsx.
 * - `ALLOWED_ROLES` drives the `allowedRoles` prop on every ProtectedRoute
 *   in App.tsx that restricts access to specific roles.
 *
 * If you add or change where a role lands after login, update this file —
 * both the redirect target and the `allowedRoles` for that destination are
 * defined together here, so they can never silently drift apart.
 */

export type AppRole =
  | "admin"
  | "dispatcher"
  | "driver"
  | "patient"
  | "supporter"
  | "employer_admin"
  | "hospital_partner_admin";

/**
 * Roles permitted on each key protected destination.
 * Import and spread into `allowedRoles` in App.tsx — do not duplicate these arrays.
 */
export const ALLOWED_ROLES = {
  /** /admin-control */
  adminControl: ["admin", "dispatcher"] as AppRole[],
  /** /employer and /small-business/dashboard */
  employer: ["employer_admin"] as AppRole[],
  /** /partner-portal and /partner-portal/:id */
  partnerPortal: ["hospital_partner_admin", "admin"] as AppRole[],
  /** /admin (super-admin only) */
  superAdmin: ["admin"] as AppRole[],
  /** /audit */
  audit: ["admin", "dispatcher"] as AppRole[],
  /** /reports */
  reports: ["admin", "dispatcher", "patient", "employer_admin"] as AppRole[],
  /** /analytics */
  analytics: ["admin", "dispatcher", "employer_admin"] as AppRole[],
  /** /admin/hospital-partners */
  adminHospitalPartners: ["dispatcher", "admin"] as AppRole[],
  /** /admin/condition-wallets and /admin-condition-wallets */
  adminConditionWallets: ["admin"] as AppRole[],
  /** /admin/errors */
  adminErrors: ["admin"] as AppRole[],
} as const;

/**
 * Returns the path the user should be sent to immediately after a successful
 * login (or on auth-page mount when already authenticated).
 *
 * Accepts `role` as `string` because that is what the Drizzle-generated User
 * type exposes (varchar columns are inferred as `string`). The function
 * narrows internally using the typed ALLOWED_ROLES arrays.
 *
 * Mirrors the allowedRoles declared in ALLOWED_ROLES — any change here
 * should be accompanied by a matching change to ALLOWED_ROLES above.
 *
 * Special cases that cannot be expressed as a simple role→path mapping
 * (e.g., the supporter onboarding gate) are handled explicitly below.
 */
export function getPostLoginRedirect(
  role: string,
  onboardingCompleted?: boolean | null,
): string {
  if ((ALLOWED_ROLES.adminControl as readonly string[]).includes(role)) {
    // admin + dispatcher both land on the admin control panel
    return "/admin-control";
  }

  if (role === "driver") {
    // Drivers land on the home / hub page
    return "/";
  }

  if ((ALLOWED_ROLES.employer as readonly string[]).includes(role)) {
    return "/employer";
  }

  if (role === "hospital_partner_admin") {
    // ProtectedRoute's autoRedirectHospitalPartners handles the actual redirect
    // from "/"; returning "/" here is consistent with that flow.
    return "/";
  }

  if (role === "supporter" && !onboardingCompleted) {
    // Supporters who haven't finished onboarding go through the onboarding flow.
    // Note: the Router in App.tsx hard-gates them to /onboarding until complete.
    return "/onboarding";
  }

  // Default: patients, completed supporters, and any unknown future roles
  return "/wallet";
}
